import { useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useLoginUsers, useSelectedOrg } from '../hooks/federatedAuthHooks';
import { getLegacyOrgHint } from '../utils/legacyOrgHint';
import { CenterContainerFederated } from './CenterContainerFederated';
import { UserAccountList } from './UserAccountList';
import { UserNotFound } from './UserNotFound';

const MAX_INLINE_ORGS = 20;

export const SwitchUserFederated = () => {
  const { data: users, isLoading } = useLoginUsers();
  const { selectOrg, rejectedOrgIds } = useSelectedOrg();
  const { loginWithRedirect } = useAuth0();

  // Excludes orgs that have already failed to resolve to a Delogue user this session. Without
  // this, a single-org account whose org fails to resolve auto-re-selects that same org the
  // instant "Choose another organization" clears the selection, looping straight back into the
  // identical failure instead of surfacing a dead end (DL-61312 round 3).
  const selectableUsers = useMemo(
    () =>
      users?.filter(
        u => !u.organization.auth0OrgId || !rejectedOrgIds.has(u.organization.auth0OrgId)
      ),
    [users, rejectedOrgIds]
  );

  useEffect(() => {
    if (selectableUsers && selectableUsers.length === 1) {
      const auth0OrgId = selectableUsers[0].organization.auth0OrgId;
      if (auth0OrgId) selectOrg(auth0OrgId);
    }
  }, [selectableUsers, selectOrg]);

  // DL-61835 (QA 2507004): legacy already resolved an org for this session and passed it via
  // `_auth0org` on the "Try Delogue 2.0" link (getLegacyOrgHint). If it matches one of this
  // user's accessible orgs, follow it instead of showing D2's own multi-org picker — otherwise
  // the legacy->D2 hop always re-prompts even though the org context is already known.
  const hintedOrgId = useMemo(() => {
    const legacyOrgHint = getLegacyOrgHint();
    if (!legacyOrgHint || !selectableUsers) return null;
    const isAccessible = selectableUsers.some(u => u.organization.auth0OrgId === legacyOrgHint);
    return isAccessible ? legacyOrgHint : null;
  }, [selectableUsers]);

  useEffect(() => {
    if (hintedOrgId) selectOrg(hintedOrgId);
  }, [hintedOrgId, selectOrg]);

  // >20 orgs: delegate to Auth0's built-in org picker via prompt=login
  useEffect(() => {
    if (!hintedOrgId && selectableUsers && selectableUsers.length > MAX_INLINE_ORGS) {
      loginWithRedirect({ authorizationParams: { prompt: 'login' } });
    }
  }, [hintedOrgId, selectableUsers, loginWithRedirect]);

  const canAutoSelect =
    Boolean(hintedOrgId) ||
    (selectableUsers?.length === 1 && Boolean(selectableUsers[0].organization.auth0OrgId));
  const isBusy =
    isLoading ||
    (selectableUsers !== undefined && (canAutoSelect || selectableUsers.length > MAX_INLINE_ORGS));

  if (isBusy) {
    return (
      <FullscreenCenter>
        <CircularProgress />
      </FullscreenCenter>
    );
  }

  const handleSelect = (userId: string) => {
    const user = selectableUsers?.find(u => u.id === userId);
    const auth0OrgId = user?.organization.auth0OrgId;
    if (auth0OrgId) selectOrg(auth0OrgId);
  };

  return (
    <CenterContainerFederated>
      {!selectableUsers || selectableUsers.length === 0 ? (
        <UserNotFound />
      ) : (
        <UserAccountList users={selectableUsers} onSelect={handleSelect} />
      )}
    </CenterContainerFederated>
  );
};

const FullscreenCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background-color: ${p => p.theme.palette.bg.secondary};
`;
